import { useEffect, useState } from 'react';
import {
	ChargeStateXref,
	ChargeStateXrefSchema,
	update,
} from '@/api/fee/charge-state-xref';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { states } from '@/constants/states';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateChargeStateXref({
	chargeStateXref: chargeStateXref,
}: {
	chargeStateXref: ChargeStateXref;
}) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedChargeStateXref: ChargeStateXref) =>
			update(updatedChargeStateXref),
		onSuccess: () => {
			toast.success(`Success! State mapping has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: ['state-mappings'],
			});
			reset();
		},
		onError: () => {
			toast.error(`Error! State mapping could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<ChargeStateXref>({
		resolver: zodResolver(ChargeStateXrefSchema),
		mode: 'all',
		defaultValues: chargeStateXref,
	});

	const { reset } = form;
	useEffect(() => {
		reset(chargeStateXref);
	}, [chargeStateXref, reset]);
	const onSubmit: SubmitHandler<ChargeStateXref> = (values: ChargeStateXref) =>
		mutate(values);

	return (
		<>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update State Mapping</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Update State Mapping</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="feeDescId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>FeeDescID</FormLabel>
											<FormControl>
												<Input
													{...field}
													value={field.value ?? ''}
													type="number"
													onChange={(e) => {
														const val = e.target.value;
														field.onChange(val === '' ? undefined : Number(val));
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="state"
									render={({ field }) => (
										<FormItem>
											<FormLabel>State</FormLabel>
											<FormControl>
												<Select
													value={field.value ?? 'none'}
													onValueChange={(val) =>
														field.onChange(val === 'none' ? null : val)
													}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a state..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>States</SelectLabel>
															{states!.map((state) => (
																<SelectItem key={state} value={state}>
																	{state}
																</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="isExpired"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
											<div className="space-y-0.5">
												<FormLabel>Expired</FormLabel>
											</div>
											<FormControl>
												<Switch checked={!!field.value} onCheckedChange={field.onChange} />
											</FormControl>
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Updating...' : 'Update'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</>
	);
}
